import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import ProfileDropdown from '../components/ProfileDropdown';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import API_BASE from '../utils/api';
import getProfilePictureSrc from '../utils/profilePicture';

const ROLE_LABELS = { admin: 'Admin', principal: 'Principal', class_teacher: 'Teacher' };

const sameId = (a, b) => a?.toString() === b?.toString();

const Avatar = ({ name, picture, size = 10 }) => (
  picture ? (
    <img src={getProfilePictureSrc(picture)} alt={name}
      className={`w-${size} h-${size} rounded-full object-cover`} />
  ) : (
    <div className={`w-${size} h-${size} rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 shrink-0`}>
      {name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
    </div>
  )
);

const Chat = () => {
  const { user } = useAuth();
  const { socket, subscribe, decrementUnread, refreshUnreadCount, isOnline } = useChat();

  const [contacts, setContacts] = useState([]);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [unreadMap, setUnreadMap] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatError, setChatError] = useState('');

  // Mobile: 'contacts' | 'chat'
  const [mobileView, setMobileView] = useState('contacts');

  const messagesEndRef = useRef(null);
  const activeContactRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    activeContactRef.current = activeContact;
  }, [activeContact]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load contacts and immediately request the current online list
  useEffect(() => {
    setLoadingContacts(true);
    axios.get(`${API_BASE}/chat/contacts`)
      .then(res => { setContacts(res.data || []); setChatDisabled(false); })
      .catch(err => {
        if (err.response?.data?.code === 'CHAT_DISABLED') setChatDisabled(true);
      })
      .finally(() => setLoadingContacts(false));

    socket.current?.emit('get_online_users');
  }, [socket]);

  // Subscribe to incoming messages from ChatContext
  useEffect(() => {
    const unsubscribe = subscribe((msg, { isForMe, isFromMe }) => {
      const ac = activeContactRef.current;
      if (!ac) return;

      const msgSender = msg.senderId?.toString?.() || msg.senderId;
      const msgReceiver = msg.receiverId?.toString?.() || msg.receiverId;
      const acId = ac._id?.toString();

      const belongsToConvo = sameId(msgSender, acId) || sameId(msgReceiver, acId);

      if (belongsToConvo) {
        setMessages(prev => [...prev, msg]);
        if (isForMe) decrementUnread(1);
      } else if (isForMe) {
        setUnreadMap(prev => ({ ...prev, [msgSender]: (prev[msgSender] || 0) + 1 }));
      }
    });
    return unsubscribe;
  }, [subscribe, decrementUnread]);

  // Listen for chat_error from socket
  useEffect(() => {
    const sock = socket.current;
    if (!sock) return;
    const handler = ({ message }) => setChatError(message);
    sock.on('chat_error', handler);
    return () => sock.off('chat_error', handler);
  }, [socket]);

  const loadMessages = useCallback(async (contact) => {
    setLoadingMessages(true);
    setMessages([]);
    setUnreadMap(prev => ({ ...prev, [contact._id]: 0 }));
    try {
      const res = await axios.get(`${API_BASE}/chat/messages/${contact._id}`);
      setMessages(res.data || []);
      // Backend just marked these as read — sync the global badge count
      refreshUnreadCount();
    } catch (_) {}
    finally { setLoadingMessages(false); }
  }, [refreshUnreadCount]);

  const handleSelectContact = (contact) => {
    setActiveContact(contact);
    loadMessages(contact);
    setMobileView('chat');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !activeContact || !socket.current) return;
    socket.current.emit('send_message', { receiverId: activeContact._id, content: text });
    setInput('');
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
  };

  const groupedMessages = messages.reduce((g, msg) => {
    const day = formatDay(msg.createdAt);
    if (!g[day]) g[day] = [];
    g[day].push(msg);
    return g;
  }, {});

  const activeOnline = activeContact ? isOnline(activeContact._id) : false;

  // ── layout ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top nav — only visible when NOT in mobile chat view */}
        <nav className={`bg-white shadow-sm shrink-0 ${mobileView === 'chat' ? 'hidden lg:block' : 'block'}`}>
          <div className="px-4 lg:px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-xl lg:text-2xl font-semibold text-gray-800">Internal Chat</h1>
              <p className="text-xs lg:text-sm text-gray-500 hidden sm:block">Secure messaging for staff</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        {/* Chat body */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Contacts panel ─────────────────────────────────────────────── */}
          <div className={`
            flex-col border-r bg-white overflow-hidden
            w-full lg:w-72 lg:shrink-0 lg:flex
            ${mobileView === 'contacts' ? 'flex' : 'hidden lg:flex'}
          `}>
            <div className="flex flex-col h-full">
              <div className="px-4 py-3 border-b bg-white">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Staff Members ({contacts.length})
                </p>
              </div>

              <div className="flex-1 overflow-y-auto bg-white">
                {loadingContacts ? (
                  <div className="p-8 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                    Loading...
                  </div>
                ) : chatDisabled ? (
                  <div className="p-6 text-center text-gray-500 text-sm mt-4">
                    <div className="text-4xl mb-2">🔒</div>
                    <p className="font-medium text-gray-700">Chat not enabled</p>
                    <p className="mt-1 text-gray-400 text-xs">Ask your admin to enable chat access for your account.</p>
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm mt-4">
                    <div className="text-4xl mb-2">👥</div>
                    No staff found
                  </div>
                ) : contacts.map(contact => {
                  const online = isOnline(contact._id);
                  const unread = unreadMap[contact._id?.toString()] || 0;
                  return (
                    <button
                      key={contact._id}
                      onClick={() => handleSelectContact(contact)}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition text-left
                        ${activeContact?._id === contact._id
                          ? 'bg-indigo-50 border-l-4 border-l-indigo-500'
                          : unread > 0
                            ? 'bg-amber-50 border-l-4 border-l-amber-400'
                            : ''}`}
                    >
                      <div className="relative shrink-0">
                        <Avatar name={contact.name} picture={contact.profilePicture} size={10} />
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white transition-colors ${online ? 'bg-green-400 ring-1 ring-green-300' : 'bg-gray-300'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>{contact.name}</p>
                          <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {ROLE_LABELS[contact.role] || contact.role}
                          {contact.messageCount > 0 && (
                            <span className="ml-1 text-gray-400">· {contact.messageCount} msg{contact.messageCount === 1 ? '' : 's'}</span>
                          )}
                          {contact.role === 'class_teacher' && !contact.chatEnabled && (
                            <span className="ml-1 text-amber-500">· chat disabled</span>
                          )}
                        </p>
                      </div>
                      {unread > 0 && (
                        <span className="bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Message panel ──────────────────────────────────────────────── */}
          <div className={`
            flex-1 flex flex-col overflow-hidden
            ${mobileView === 'chat' ? 'flex' : 'hidden lg:flex'}
          `}>
            {!activeContact ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-400 px-6">
                  <div className="text-6xl mb-4">💬</div>
                  <p className="text-lg font-medium text-gray-600">Select a contact</p>
                  <p className="text-sm mt-1">Choose someone from the left to start chatting</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Chat header */}
                <div className="bg-white border-b px-4 py-3 flex items-center gap-3 shadow-sm shrink-0">
                  {/* Back button — mobile only */}
                  <button
                    onClick={() => { setMobileView('contacts'); setActiveContact(null); }}
                    className="lg:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700"
                    aria-label="Back to contacts"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <div className="relative shrink-0">
                    <Avatar name={activeContact.name} picture={activeContact.profilePicture} size={9} />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${activeOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">{activeContact.name}</p>
                      <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${activeOnline ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {activeOnline ? '● Online' : '○ Offline'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{ROLE_LABELS[activeContact.role] || activeContact.role}</p>
                  </div>
                </div>

                {/* Error banner */}
                {chatError && (
                  <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex justify-between">
                    {chatError}
                    <button onClick={() => setChatError('')} className="text-red-400 hover:text-red-600">✕</button>
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50 space-y-1">
                  {loadingMessages ? (
                    <div className="flex justify-center items-center h-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : (
                    <>
                      {Object.entries(groupedMessages).map(([day, dayMsgs]) => (
                        <div key={day}>
                          <div className="flex items-center my-4">
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="mx-3 text-xs text-gray-400 font-medium bg-gray-50 px-2">{day}</span>
                            <div className="flex-1 h-px bg-gray-200" />
                          </div>
                          {dayMsgs.map(msg => {
                            const isMine = sameId(msg.senderId, user?.id) || sameId(msg.senderId, user?._id);
                            return (
                              <div key={msg._id} className={`flex mb-1.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                                {!isMine && (
                                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 shrink-0 mr-2 mt-1 self-end">
                                    {msg.senderName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 1)}
                                  </div>
                                )}
                                <div className={`max-w-[75%] sm:max-w-sm px-3.5 py-2 rounded-2xl text-sm shadow-sm
                                  ${isMine ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm'}`}
                                >
                                  <p className="break-words leading-relaxed">{msg.content}</p>
                                  <p className={`text-right text-[10px] mt-1 ${isMine ? 'text-indigo-200' : 'text-gray-400'}`}>
                                    {formatTime(msg.createdAt)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      {messages.length === 0 && !loadingMessages && (
                        <div className="flex items-center justify-center h-full pt-16">
                          <p className="text-gray-400 text-sm">No messages yet. Say hello! 👋</p>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Input bar */}
                <form
                  onSubmit={handleSend}
                  className="bg-white border-t px-3 py-3 flex items-center gap-2 shrink-0"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder={`Message ${activeContact.name}...`}
                    className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition"
                    maxLength={1000}
                    disabled={chatDisabled}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || chatDisabled}
                    className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center hover:bg-indigo-700 disabled:opacity-40 transition active:scale-95 shrink-0"
                  >
                    <svg className="w-5 h-5 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </form>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Chat;
